import Papa from "papaparse"

import {
  type InvalidParsedRow,
  parsedCsvRowSchema,
  type ParsedRow,
  type ValidParsedRow,
} from "./schemas"

function parseNoCallCsv(
  file: File,
): Promise<{ errors: string[]; rows: ParsedRow[] }> {
  return new Promise((resolve) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const errors = results.errors.map((e) => e.message)
        const rows = (results.data as Record<string, unknown>[]).map(processRow)
        const deduped = markDuplicates(rows)

        resolve({ errors, rows: deduped })
      },
      error: (error) => {
        resolve({ errors: [error.message], rows: [] })
      },
    })
  })
}

function processRow(raw: Record<string, unknown>, index: number): ParsedRow {
  const phone = normalizeHeader(raw, ["Phone number", "phone_number", "phone"])
  const email = normalizeHeader(raw, ["Email", "email"])
  const noCall = normalizeHeader(raw, ["No call", "no_call", "noCall"])
  const rowIndex = index + 1

  const result = parsedCsvRowSchema.safeParse({
    email,
    noCall,
    phone,
    rowIndex,
  })

  if (!result.success) {
    const errors = result.error.issues.map((i) => i.message)

    // We ignore all errors from converting to String and Boolean here.
    // e.g. String(object), Boolean("false") etc.
    // What we really want is the errors to log in the CSV file to download.
    return {
      email: String(email),
      errors,
      noCall: noCall === undefined ? undefined : String(noCall),
      parseStatus: "invalid",
      phone: String(phone),
      rowIndex,
    } satisfies InvalidParsedRow
  }

  return {
    ...result.data,
    parseStatus: "valid",
  } satisfies ValidParsedRow
}

function normalizeHeader(
  row: Record<string, unknown>,
  candidates: string[],
): unknown {
  const lowerMap = new Map(
    Object.entries(row).map(([key, value]) => [
      key.toLowerCase().trim(),
      value,
    ]),
  )

  for (const key of candidates) {
    const val = lowerMap.get(key.toLowerCase())

    if (val !== undefined) {
      return val
    }
  }

  return undefined
}

function markDuplicates(rows: ParsedRow[]): ParsedRow[] {
  const seenPhones = new Set<string>()
  const seenEmails = new Set<string>()

  return rows.map((row) => {
    if (row.parseStatus === "invalid") {
      return row
    }

    const errors: string[] = []

    if (row.phone) {
      if (seenPhones.has(row.phone)) {
        errors.push("PHONE_NUMBER_DUPLICATE")
      } else {
        seenPhones.add(row.phone)
      }
    }

    if (row.email) {
      const lowerEmail = row.email.toLowerCase()
      if (seenEmails.has(lowerEmail)) {
        errors.push("EMAIL_DUPLICATE")
      } else {
        seenEmails.add(lowerEmail)
      }
    }

    if (errors.length > 0) {
      return {
        email: row.email,
        errors,
        noCall: row.noCall,
        parseStatus: "invalid" as const,
        phone: row.phone,
        rowIndex: row.rowIndex,
      }
    }

    return row
  })
}

export { parseNoCallCsv }
